/**
 * pageConditions.ts
 *
 * Manages conditional visibility logic for form pages.
 * Evaluates page conditions based on form values and dependencies.
 *
 * Condition System:
 * - Page dependencies on field values
 * - Value comparison
 * - Multiple condition handling
 * - Dynamic evaluation
 * - Navigation-aware evaluation
 *
 * Supported Operations:
 * - Equals/Not Equals
 * - Contains/Not Contains
 * - Greater Than/Less Than
 * - AND/OR logic
 *
 * Features:
 * - Real-time evaluation
 * - Multiple field dependencies
 * - Type-safe comparisons
 * - Default visibility
 * - Navigation utilities
 * - Error handling
 *
 * Integration:
 * - Form rendering
 * - Page navigation
 * - Dynamic form flow
 * - Progress calculation
 */

import { Page, FieldCondition, Field } from "../types/types";

/**
 * Finds which page contains a given field
 * @param pages All pages in the form
 * @param fieldId The field ID to search for
 * @returns The page containing the field, or undefined
 */
const findPageContainingField = (pages: Page[], fieldId: string, allFields: Field[]): Page | undefined => {
  const field = allFields.find((f) => f.id === fieldId);
  if (!field?.pageId) return undefined;
  return pages.find((p) => p.id === field.pageId);
};

/**
 * Detects circular dependencies between pages in conditional logic
 * A circular dependency occurs when page A's visibility depends on a field from page B,
 * and page B's visibility depends on a field from page A (directly or indirectly)
 *
 * @param pages All pages in the form
 * @param allFields All fields in the form
 * @returns Array of error messages describing detected circular dependencies
 */
export const detectCircularPageDependencies = (pages: Page[], allFields: Field[]): string[] => {
  const errors: string[] = [];
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  const getPageDependencies = (page: Page): Page[] => {
    if (!page.conditions || page.conditions.length === 0) return [];

    return page.conditions
      .map((cond) => findPageContainingField(pages, cond.fieldId, allFields))
      .filter((p): p is Page => p !== undefined && p.id !== page.id);
  };

  const dfs = (pageId: string, path: string[] = []): void => {
    if (recursionStack.has(pageId)) {
      const circular = [...path, pageId];
      const pageOrders = circular.map((pid) => pages.find((p) => p.id === pid)?.order ?? "?");
      errors.push(
        `Circular dependency detected: Pages ${pageOrders.join(" → ")} have interdependent conditions`
      );
      return;
    }

    if (visited.has(pageId)) return;

    visited.add(pageId);
    recursionStack.add(pageId);

    const page = pages.find((p) => p.id === pageId);
    if (page) {
      const dependencies = getPageDependencies(page);
      dependencies.forEach((depPage) => {
        dfs(depPage.id, [...path, pageId]);
      });
    }

    recursionStack.delete(pageId);
  };

  pages.forEach((page) => {
    if (!visited.has(page.id)) {
      dfs(page.id);
    }
  });

  return errors;
};

/**
 * Evaluates page conditions to determine visibility
 *
 * @param page The page to evaluate conditions for
 * @param formValues Current form values as a record object
 * @param allFields All fields in the form
 * @returns boolean indicating whether the page should be visible
 *
 * @remarks
 * - If page has no conditions: page is always visible (returns true)
 * - If page.showIf === "any": page is visible if ANY condition is true (OR logic)
 * - If page.showIf === "all" or undefined: page is visible if ALL conditions are true (AND logic, default)
 * - If a condition references a non-existent field: that condition evaluates to false
 * - Date comparisons that fail to parse default to false
 * - Other evaluation errors default to false with warning logged
 *
 * @example
 * const isVisible = evaluatePageConditions(page, answers, fields);
 * if (!isVisible) {
 *   // Skip this page in navigation
 * }
 */
export const evaluatePageConditions = (
  page: Page,
  formValues: Record<string, any>,
  allFields: Field[],
): boolean => {
  const conditions = page.conditions;
  if (!conditions || conditions.length === 0) {
    return true;
  }

  const checkCondition = (condition: FieldCondition): boolean => {
    const sourceField = allFields.find((f) => f.id === condition.fieldId);
    if (!sourceField) {
      console.warn(
        `[evaluatePageConditions] Condition references non-existent field: ${condition.fieldId}. ` +
        `Available fields: ${allFields.map((f) => f.id).join(", ")}`
      );
      return false;
    }

    let fieldValue = formValues[condition.fieldId];

    // Handle nested answer objects that contain { type, value, userId, timestamp }
    if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
      fieldValue = fieldValue.value;
    }

    switch (condition.operator) {
      case "equals":
        return fieldValue === condition.value;
      case "notEquals":
        return fieldValue !== condition.value;
      case "contains":
        return String(fieldValue).includes(String(condition.value));
      case "notContains":
        return !String(fieldValue).includes(String(condition.value));
      case "greater":
        return Number(fieldValue) > Number(condition.value);
      case "less":
        return Number(fieldValue) < Number(condition.value);
      case "greaterOrEqual":
        return Number(fieldValue) >= Number(condition.value);
      case "lessOrEqual":
        return Number(fieldValue) <= Number(condition.value);
      case "isEmpty":
        return !fieldValue || fieldValue === "" || fieldValue === null || fieldValue === undefined;
      case "isNotEmpty":
        return fieldValue && fieldValue !== "" && fieldValue !== null && fieldValue !== undefined;
      case "before":
        // Date comparison: fieldValue is before condition.value
        try {
          const fieldDate = new Date(fieldValue);
          const compareDate = new Date(condition.value as string);
          return fieldDate < compareDate;
        } catch {
          return false;
        }
      case "after":
        // Date comparison: fieldValue is after condition.value
        try {
          const fieldDate = new Date(fieldValue);
          const compareDate = new Date(condition.value as string);
          return fieldDate > compareDate;
        } catch {
          return false;
        }
      case "between":
        // Date/number is between two values
        // condition.value should be an array [min, max]
        try {
          if (Array.isArray(condition.value) && condition.value.length === 2) {
            const numValue = Number(fieldValue);
            const min = Number(condition.value[0]);
            const max = Number(condition.value[1]);
            return numValue >= min && numValue <= max;
          }
          return false;
        } catch {
          return false;
        }
      default:
        return false;
    }
  };

  // If showIf is not specified, default to requiring all conditions to be met
  return page.showIf === "any"
    ? conditions.some(checkCondition)
    : conditions.every(checkCondition);
};

/**
 * Finds the next visible page in a given direction
 * @param currentPageIndex Current page index
 * @param allPages All pages in the form
 * @param formValues Current form values
 * @param allFields All fields in the form
 * @param direction Direction to navigate (1 for forward, -1 for backward)
 * @returns Index of next visible page or null if none found
 */
export const findNextVisiblePage = (
  currentPageIndex: number,
  allPages: Page[],
  formValues: Record<string, any>,
  allFields: Field[],
  direction: 1 | -1 = 1,
): number | null => {
  const startIndex = currentPageIndex + direction;

  if (direction === 1) {
    // Forward direction
    for (let i = startIndex; i < allPages.length; i++) {
      if (evaluatePageConditions(allPages[i], formValues, allFields)) {
        return i;
      }
    }
  } else {
    // Backward direction
    for (let i = startIndex; i >= 0; i--) {
      if (evaluatePageConditions(allPages[i], formValues, allFields)) {
        return i;
      }
    }
  }

  return null;
};

/**
 * Gets a list of all currently visible page indices
 * @param allPages All pages in the form
 * @param formValues Current form values
 * @param allFields All fields in the form
 * @returns Array of visible page indices
 */
export const getVisiblePages = (
  allPages: Page[],
  formValues: Record<string, any>,
  allFields: Field[],
): number[] => {
  const visibleIndices: number[] = [];

  for (let i = 0; i < allPages.length; i++) {
    if (evaluatePageConditions(allPages[i], formValues, allFields)) {
      visibleIndices.push(i);
    }
  }

  return visibleIndices;
};
